import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  CreateDataConnectionInput,
  DataConnection,
  DataConnectionSchemaStatus,
  IntrospectDataConnectionResult,
  SchemaAnnotationsDelta,
  TestDataConnectionResult,
  UpdateDataConnectionInput,
} from "@/api/data-connections-api"

import {
  createDataConnection,
  deleteDataConnection,
  getDataConnectionSchema,
  introspectDataConnection,
  listDataConnections,
  testDataConnection,
  updateDataConnection,
  updateDataConnectionAnnotations,
} from "@/api/data-connections-api"

export const DATA_CONNECTIONS_KEY = ["admin", "dataConnections"] as const

const STALE_TIME = 30_000
const GC_TIME = 5 * 60 * 1000

export function useDataConnectionsQuery(enabled = true) {
  return useQuery<DataConnection[]>({
    queryKey: DATA_CONNECTIONS_KEY,
    queryFn: listDataConnections,
    enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useCreateDataConnectionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateDataConnectionInput) => createDataConnection(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DATA_CONNECTIONS_KEY })
    },
  })
}

export function useUpdateDataConnectionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDataConnectionInput }) =>
      updateDataConnection(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DATA_CONNECTIONS_KEY })
    },
  })
}

export function useDeleteDataConnectionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteDataConnection(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DATA_CONNECTIONS_KEY })
    },
  })
}

export function useTestDataConnectionMutation() {
  return useMutation<TestDataConnectionResult, Error, string>({
    mutationFn: (id: string) => testDataConnection(id),
  })
}

export function useIntrospectDataConnectionMutation() {
  const queryClient = useQueryClient()
  return useMutation<IntrospectDataConnectionResult, Error, string>({
    mutationFn: (id: string) => introspectDataConnection(id),
    onSuccess: (_result, id) => {
      // Introspection is scheduled, not instant — invalidate the schema
      // query after a small delay so the row flips to "Introspecting…"
      // and then re-fetches once the crawl typically completes.
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ["admin", "dataConnectionSchema", id] })
      }, 5000)
    },
  })
}

export function useDataConnectionSchemaQuery(id: string | undefined, enabled = true) {
  return useQuery<DataConnectionSchemaStatus | null>({
    queryKey: ["admin", "dataConnectionSchema", id],
    queryFn: () => (id ? getDataConnectionSchema(id) : Promise.resolve(null)),
    enabled: enabled && !!id,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    // Poll briefly after an introspect kick — refetchInterval gets
    // cleared once we see a non-null status so we're not polling idle.
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data || !data.status) return 10_000
      return false
    },
  })
}

export function useUpdateSchemaAnnotationsMutation() {
  const queryClient = useQueryClient()
  return useMutation<
    DataConnectionSchemaStatus,
    Error,
    { id: string; delta: SchemaAnnotationsDelta }
  >({
    mutationFn: ({ id, delta }) => updateDataConnectionAnnotations(id, delta),
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(["admin", "dataConnectionSchema", id], data)
    },
  })
}
